<template>
  <!-- Wait for permissions before rendering to avoid a 403 flash -->
  <div v-if="!permsLoaded"></div>

  <PageError
    v-else-if="!canViewAlertContact"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />

  <PageError v-else-if="error" :error="error" :errorCode="errorCode" />

  <!-- Full-page loader until the first load finishes, so the page mounts
       populated instead of flashing empty then filling in. -->
  <PageLoader v-else-if="loading" />

  <div v-else class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Alert Contacts - {{ filteredItems.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>

      <div class="mb-4 text-base flex space-x-3 justify-between">
        <button
          v-if="canChangeAlertContact"
          id="refresh-alert-groups-btn"
          @click="refreshGroups"
          :disabled="groupsRefreshing"
          class="btn bg-gray-600 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ groupsRefreshing ? "Refreshing Groups..." : "Refresh Groups" }}
        </button>
        <button
          v-if="canAddAlertContact"
          id="add-alert-contact-btn"
          @click="goToAdd"
          class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Alert Contact
        </button>
      </div>
    </div>

    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading Alert Contacts..."
      @select="handleOrgSelect"
    />

    <!-- Search and Bulk Action Section -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar v-model="searchQuery" placeholderText="Search by name or mobile number..." />
      </div>

      <div class="flex items-center mt-4 md:mt-0 space-x-2">
        <GlassSelect
          v-model="bulkAction"
          :options="bulkActionOptions"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedItems.length === 0"
          :class="[
            'btn focus:outline-none focus:ring-2 focus:ring-blue-500',
            bulkAction && selectedItems.length
              ? bulkAction === 'delete'
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Table -->
    <GenericTable
      id="alert-contacts-table"
      ref="table"
      :columns="columns"
      :items="paginatedItems"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedItems"
      @rowClick="handleRowClick"
    >
      <template #msisdn="{ value }">
        {{ value ? displayMsisdn(value) : "N/A" }}
      </template>
      <template #groups="{ item }">
        <div v-if="groupPillsFor(item).length" class="flex flex-wrap items-center gap-1">
          <span
            v-for="pill in groupPillsFor(item).slice(0, 2)"
            :key="pill.key"
            class="inline-flex items-center rounded-full bg-blue-600/10 dark:bg-blue-500/20 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300"
          >
            {{ pill.label }}
          </span>
          <span
            v-if="groupPillsFor(item).length > 2"
            :title="groupPillsFor(item).map((pill) => pill.label).join(', ')"
            class="inline-flex items-center rounded-full bg-gray-500/10 dark:bg-gray-400/20 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300"
          >
            +{{ groupPillsFor(item).length - 2 }}
          </span>
        </div>
        <span v-else class="text-xs font-medium text-amber-600 dark:text-amber-400">
          No groups
        </span>
      </template>
      <template #org>
        {{ orgName }}
      </template>
      <template #created_date="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0">
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 dark:text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="selectedObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem = false"
      :isChildObject="true"
      :count="count"
    />

    <!-- Bulk Assign to Groups Modal -->
    <div
      v-if="assigning"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      @click.self="closeAssign"
    >
      <div class="glass-card w-full max-w-lg p-6 max-h-[80vh] flex flex-col">
        <div class="flex items-center gap-2 mb-4 shrink-0">
          <button
            type="button"
            @click="assignMode = 'add'"
            :class="[
              'px-3 py-1 text-sm rounded-full focus:outline-none',
              assignMode === 'add'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
            ]"
          >
            Add to groups
          </button>
          <button
            type="button"
            @click="assignMode = 'remove'"
            :class="[
              'px-3 py-1 text-sm rounded-full focus:outline-none',
              assignMode === 'remove'
                ? 'bg-red-600 text-white'
                : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
            ]"
          >
            Remove from groups
          </button>
        </div>

        <div class="flex-1 min-h-0 overflow-y-auto">
          <AlertGroupPicker
            v-model="assignGroupIds"
            :groups="groups"
          />
        </div>

        <div class="flex justify-end gap-2 mt-6 shrink-0">
          <button type="button" @click="closeAssign" class="btn-cancel">Cancel</button>
          <button
            type="button"
            @click="confirmAssign"
            :disabled="assignGroupIds.length === 0 || isAssigning"
            :class="[
              'btn text-white focus:outline-none focus:ring-2 focus:ring-blue-500',
              assignGroupIds.length === 0 || isAssigning
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : assignMode === 'remove'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-blue-600 hover:bg-blue-700',
            ]"
          >
            {{ isAssigning ? "Applying..." : "Apply" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getAllAlertContacts,
  deleteAlertContact,
  updateAlertContact,
  getAllAlertGroups,
  refreshAlertGroups,
} from "@/services/alertContactService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import PageError from "@/components/PageError.vue";
import { formatError, errorMessages } from "@/utils/errorMessages";
import { useStore } from "@/store";
import DeleteModal from "@/components/DeleteModal.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import AlertGroupPicker from "@/components/AlertGroupPicker.vue";
import PageLoader from "@/components/PageLoader.vue";
import { formatMsisdnForDisplay, loadPhoneNumberUtils } from "@/utils/phoneNumber";

export default {
  name: "AlertContactsView",
  components: {
    SearchBar,
    GenericTable,
    PageError,
    PageLoader,
    OrganizationTabs,
    DeleteModal,
    GlassSelect,
    AlertGroupPicker,
  },
  data() {
    return {
      loading: true,
      items: [],
      groups: [],
      searchQuery: "",
      filteredItems: [],
      currentPage: 1,
      itemsPerPage: 10,
      selectedItems: [],
      deletingItem: false,
      bulkAction: "",
      assigning: false,
      groupsRefreshing: false,
      assignMode: "add",
      assignGroupIds: [],
      isAssigning: false,
      columns: [
        { key: "name", label: "Name", sortable: true, width: "22%" },
        { key: "msisdn", label: "Mobile Number", sortable: true, width: "20%" },
        { key: "groups", label: "Alert Groups", sortable: false, width: "28%" },
        { key: "org", label: "Organisation", sortable: false, width: "15%" },
        { key: "created_date", label: "Created Date", sortable: true, width: "15%" },
      ],
      errorMessages,
      error: "",
      errorCode: "",
      isDeleting: false,
    };
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    isSuperuser() {
      return Boolean(this.user?.is_superuser);
    },
    canViewAlertContact() {
      return this.isSuperuser || useStore.hasPermission("Can view alert contact");
    },
    canAddAlertContact() {
      return this.isSuperuser || useStore.hasPermission("Can add alert contact");
    },
    canChangeAlertContact() {
      return this.isSuperuser || useStore.hasPermission("Can change alert contact");
    },
    canDeleteAlertContact() {
      return this.isSuperuser || useStore.hasPermission("Can delete alert contact");
    },
    isServiceProviderUser() {
      return useStore.isServiceProviderUser();
    },
    isFiltered() {
      return this.searchQuery !== "";
    },
    orgName() {
      const id = this.tenant.currentOrganizationId.value;
      return id ? this.tenant.getOrganizationName(id) : "N/A";
    },
    bulkActionOptions() {
      const options = [];
      if (this.canChangeAlertContact) {
        options.push({ value: "assign", label: "Assign to groups" });
      }
      if (this.canDeleteAlertContact) {
        options.push({ value: "delete", label: "Delete selected items" });
      }
      return options;
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredItems.length / this.itemsPerPage) || 1;
    },
    paginatedItems() {
      if (this.itemsPerPage === -1) return this.filteredItems;
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredItems.slice(start, start + this.itemsPerPage);
    },
    selectedObjects() {
      return this.selectedItems.map((item) => ({
        name: "Alert Contact",
        value: item.name,
      }));
    },
    count() {
      return [{ name: "Alert Contacts", value: this.selectedObjects.length }];
    },
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    },
    permsLoaded: {
      handler(loaded) {
        // Only fetch once we know the user may view, so a denied user never hits the API.
        if (loaded && this.canViewAlertContact) {
          this.initData();
        }
      },
      immediate: true,
    },
  },
  methods: {
    async initData() {
      // Own loading flag: on back-navigation the org is already pinned, so
      // tenant.isLoading never flips and the table would flash empty mid-fetch.
      this.loading = true;
      try {
        await this.tenant.fetchOrganizations();
        if (this.tenant.currentOrganizationId.value) {
          await this.tenant.syncUrlParam();
          await this.fetchItems(this.tenant.currentSchemaName.value);
        } else {
          await this.tenant.loadInitialData(this.fetchItems);
        }
      } finally {
        this.loading = false;
      }
    },
    async handleOrgSelect(orgId) {
      try {
        this.error = "";
        await this.tenant.switchOrganization(orgId, this.fetchItems);
      } catch (error) {
        this.error = "Failed to switch organization";
        console.error("Organization select error:", error);
      }
    },
    async refreshGroups() {
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) return;
      this.groupsRefreshing = true;
      try {
        await refreshAlertGroups(validation.client);
        // 202: the sync runs in the background, so give it a moment before refetching
        setTimeout(async () => {
          await this.fetchItems(validation.client);
          this.groupsRefreshing = false;
        }, 4000);
      } catch (error) {
        this.groupsRefreshing = false;
        this.error =
          error?.response?.status === 429
            ? "Groups were refreshed recently. Please wait a minute and try again."
            : "Failed to refresh alert groups.";
      }
    },
    async fetchItems(schemaName = null) {
      try {
        this.error = "";
        this.errorCode = "";
        const [contacts, groups] = await Promise.all([
          getAllAlertContacts(schemaName),
          getAllAlertGroups(schemaName).catch(() => []),
          loadPhoneNumberUtils(),
        ]);
        this.items = contacts;
        this.groups = groups || [];
        this.applyFilters();
      } catch (error) {
        console.error("Error fetching alert contacts:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    groupPillsFor(contact) {
      return (contact.groups || []).map((group) => ({
        key: `${group.id}`,
        label: group.label || group.slug,
      }));
    },
    displayMsisdn(value) {
      return formatMsisdnForDisplay(value);
    },
    goToDetail(item) {
      this.$router.push({
        name: "AlertContactDetails",
        params: { id: item.id },
        query: this.tenant.addSchemaParam(),
      });
    },
    goToEdit(item) {
      this.$router.push({
        name: "EditAlertContact",
        params: { id: item.id },
        query: this.tenant.addSchemaParam(),
      });
    },
    // Row click: users who can change go straight to the edit form; view-only
    // users get the read-only details page.
    handleRowClick(item) {
      if (this.canChangeAlertContact) {
        this.goToEdit(item);
      } else {
        this.goToDetail(item);
      }
    },
    goToAdd() {
      this.$router.push({
        name: "AddAlertContact",
        query: this.tenant.addSchemaParam(),
      });
    },
    updateSelectedItems(selectedItems) {
      this.selectedItems = selectedItems.filter((item) => !item.marked_for_deletion);
    },
    applyBulkAction() {
      if (this.bulkAction === "delete") {
        this.deletingItem = true;
      } else if (this.bulkAction === "assign") {
        this.assignMode = "add";
        this.assignGroupIds = [];
        this.assigning = true;
      }
      this.bulkAction = "";
    },
    closeAssign() {
      this.assigning = false;
      this.assignGroupIds = [];
    },
    async confirmAssign() {
      if (this.selectedItems.length === 0 || this.assignGroupIds.length === 0) return;
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = "Malformed request. Failed to update alert contacts!";
        this.errorCode = "422!";
        this.assigning = false;
        return;
      }
      this.isAssigning = true;
      const pickedIds = new Set(this.assignGroupIds);
      try {
        const freshContacts = await getAllAlertContacts(validation.client);
        const freshById = new Map(
          (freshContacts || []).map((contact) => [contact.id, contact])
        );
        await Promise.all(
          this.selectedItems
            .filter((selected) => freshById.has(selected.id))
            .map((selected) => {
              const contact = freshById.get(selected.id);
              const currentIds = (contact.groups || []).map((group) => group.id);
              const nextIds =
                this.assignMode === "remove"
                  ? currentIds.filter((groupId) => !pickedIds.has(groupId))
                  : [...new Set([...currentIds, ...this.assignGroupIds])];
              return updateAlertContact(
                contact.id,
                { group_ids: nextIds },
                validation.client
              );
            })
        );
        this.$refs.table?.clearSelection();
        this.selectedItems = [];
        await this.fetchItems(validation.client);
      } catch (error) {
        console.error("Error during bulk group assignment:", error);
        alert("An error occurred while updating alert contacts. Please try again.");
      } finally {
        this.isAssigning = false;
        this.assigning = false;
        this.assignGroupIds = [];
      }
    },
    async confirmDelete() {
      this.isDeleting = true;
      await this.deleteSelectedItems();
      this.deletingItem = false;
      this.bulkAction = "";
    },
    async deleteSelectedItems() {
      if (this.selectedItems.length === 0) return;
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = "Malformed request. Failed to delete alert contacts!";
        this.errorCode = "422!";
        return;
      }
      try {
        await Promise.all(
          this.selectedItems.map((item) =>
            deleteAlertContact(item.id, validation.client)
          )
        );
        this.$refs.table?.clearSelection();
        this.selectedItems = [];
        await this.fetchItems(validation.client);
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting alert contacts. Please try again.");
      } finally {
        this.isDeleting = false;
      }
    },
    applyFilters() {
      const query = (this.searchQuery || "").toLowerCase();
      this.filteredItems = (this.items || []).filter((item) => {
        const name = (item?.name || "").toLowerCase();
        const msisdn = (item?.msisdn || "").toLowerCase();
        return name.includes(query) || msisdn.includes(query);
      });
      this.currentPage = 1;
    },
    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
  },
};
</script>
